import { useAuth } from '@/lib/store/auth';

export const PLAN_H5_SESSION_STORAGE_KEY = 'lumeseek_plan_h5_session_v1';
const H5_TICKET_SLOT = '__lumeseekH5Ticket';

type StoredPlanH5Session = {
  token: string;
  expiresAt: string;
};

type TicketWindow = Window & { __lumeseekH5Ticket?: unknown };

function readSession(): StoredPlanH5Session | null {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PLAN_H5_SESSION_STORAGE_KEY) || 'null');
    if (
      typeof parsed?.token !== 'string'
      || typeof parsed?.expiresAt !== 'string'
      || new Date(parsed.expiresAt).getTime() <= Date.now()
    ) {
      window.sessionStorage.removeItem(PLAN_H5_SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    window.sessionStorage.removeItem(PLAN_H5_SESSION_STORAGE_KEY);
    return null;
  }
}

function exchangeUrl() {
  const base = String(import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');
  return `${base}/auth/h5-session/exchange`;
}

export function getPlanH5SessionToken() {
  if (typeof window === 'undefined') return null;
  return readSession()?.token || null;
}

export function hasPlanH5Session() {
  return Boolean(getPlanH5SessionToken());
}

export function clearPlanH5Session() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PLAN_H5_SESSION_STORAGE_KEY);
}

export async function bootstrapPlanH5Session(): Promise<
  { status: 'not-requested' | 'authenticated' | 'failed' }
> {
  const ticketWindow = window as TicketWindow;
  const ticketRequested = Object.prototype.hasOwnProperty.call(ticketWindow, H5_TICKET_SLOT);
  const ticket = ticketRequested ? ticketWindow.__lumeseekH5Ticket : undefined;
  delete ticketWindow.__lumeseekH5Ticket;

  if (!ticketRequested) {
    return { status: readSession() ? 'authenticated' : 'not-requested' };
  }
  // 新协议启动时清除旧版 WebView 曾持久化的 7 天 Platform JWT，
  // 避免 ticket 兑换失败后意外回退到旧凭证。
  useAuth.getState().logout();
  clearPlanH5Session();
  if (typeof ticket !== 'string' || !ticket.trim()) return { status: 'failed' };

  try {
    const response = await fetch(exchangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket }),
      cache: 'no-store',
    });
    if (!response.ok) return { status: 'failed' };
    const result = await response.json() as StoredPlanH5Session;
    if (
      typeof result?.token !== 'string'
      || !result.token
      || typeof result?.expiresAt !== 'string'
      || new Date(result.expiresAt).getTime() <= Date.now()
    ) {
      return { status: 'failed' };
    }
    window.sessionStorage.setItem(PLAN_H5_SESSION_STORAGE_KEY, JSON.stringify(result));
    return { status: 'authenticated' };
  } catch {
    return { status: 'failed' };
  }
}
